import React from 'react'
import { AlertTriangle, RefreshCw, Home, Bug } from 'lucide-react'
import { errorMonitor } from '../lib/errorMonitor'

interface ErrorBoundaryProps {
  children: React.ReactNode
  moduleName?: string
  fallback?: React.ReactNode
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void
}

interface ErrorBoundaryState {
  hasError: boolean
  error: Error | null
  errorCount: number
  retrying: boolean
  showDetails: boolean
}

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  private retryTimer: ReturnType<typeof setTimeout> | null = null
  private retryAttempts = 0

  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = {
      hasError: false,
      error: null,
      errorCount: 0,
      retrying: false,
      showDetails: false,
    }
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error(
      `[ErrorBoundary${this.props.moduleName ? ' - ' + this.props.moduleName : ''}]`,
      error,
      errorInfo
    )
    errorMonitor.report(error, {
      component: this.props.moduleName || 'ErrorBoundary',
      severity: 'high',
      handled: true,
      context: { componentStack: errorInfo.componentStack?.slice(0, 500) },
    })
    this.props.onError?.(error, errorInfo)
  }

  componentWillUnmount() {
    if (this.retryTimer) {
      clearTimeout(this.retryTimer)
    }
  }

  handleRetry = () => {
    const maxRetries = 3
    const delay = Math.min(1000 * Math.pow(2, this.retryAttempts), 8000)
    this.retryAttempts++

    this.setState({ retrying: true })

    this.retryTimer = setTimeout(() => {
      if (this.retryAttempts >= maxRetries) {
        this.setState({ retrying: false })
      } else {
        this.setState({
          hasError: false,
          error: null,
          retrying: false,
        })
      }
    }, delay)
  }

  handleReload = () => {
    window.location.reload()
  }

  handleGoHome = () => {
    window.location.href = '/'
  }

  componentDidUpdate(_prevProps: ErrorBoundaryProps, prevState: ErrorBoundaryState) {
    if (this.state.hasError && this.state.error?.message !== prevState.error?.message) {
      this.retryAttempts = 0
    }
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback
      }

      const isMaxRetries = this.retryAttempts >= 3

      return (
        <div className="flex items-center justify-center min-h-[400px] p-8">
          <div className="text-center max-w-md">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-100 mb-6">
              <AlertTriangle className="w-8 h-8 text-red-500" />
            </div>
            <h2 className="text-xl font-semibold text-gray-800 mb-2">
              {this.props.moduleName
                ? `${this.props.moduleName} 模块加载异常`
                : '页面加载异常'}
            </h2>
            <p className="text-gray-500 mb-6 text-sm leading-relaxed">
              {this.state.retrying
                ? '正在尝试恢复...'
                : isMaxRetries
                  ? '多次重试未能恢复，建议刷新页面或联系管理员。'
                  : '模块加载时发生错误，请重试。'}
            </p>
            <div className="flex items-center justify-center gap-3">
              <button
                onClick={this.handleRetry}
                disabled={this.state.retrying || isMaxRetries}
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
              >
                <RefreshCw
                  className={`w-4 h-4 ${this.state.retrying ? 'animate-spin' : ''}`}
                />
                {this.state.retrying ? '尝试中...' : '重试'}
              </button>
              <button
                onClick={this.handleReload}
                className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium"
              >
                <RefreshCw className="w-4 h-4" />
                刷新页面
              </button>
              {window.location.pathname !== '/' && (
                <button
                  onClick={this.handleGoHome}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium"
                >
                  <Home className="w-4 h-4" />
                  回首页
                </button>
              )}
            </div>
            {this.state.error && (
              <div className="mt-4">
                <button
                  onClick={() => this.setState((s) => ({ showDetails: !s.showDetails }))}
                  className="inline-flex items-center gap-1.5 text-xs text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <Bug className="w-3.5 h-3.5" />
                  {this.state.showDetails ? '隐藏' : '查看'}错误详情
                </button>
                {this.state.showDetails && (
                  <div className="mt-2 text-left">
                    <pre className="p-3 bg-gray-50 rounded-lg text-xs text-gray-600 overflow-auto max-h-48 whitespace-pre-wrap border border-gray-100">
                      {this.state.error.message}
                      {this.state.error.stack && (
                        <>
                          {'\n\n'}
                          {this.state.error.stack.slice(0, 2000)}
                        </>
                      )}
                    </pre>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )
    }

    return this.props.children
  }
}

export function withErrorBoundary<P extends object>(
  Component: React.ComponentType<P>,
  moduleName?: string
) {
  return function WithErrorBoundary(props: P) {
    return (
      <ErrorBoundary moduleName={moduleName}>
        <Component {...props} />
      </ErrorBoundary>
    )
  }
}
